/** @format */

import { SwipeableDrawer } from '@mui/material';
import { useEffect, useMemo, useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from 'src/components';
import { DesktopPreviewIcon, MobilePreviewIcon } from 'src/components/icons';
import useDeviceDetect from 'src/hooks/useDeviceDetect';
import { mapEditorStateToPreviewDetail } from 'src/utils/contentEditor/mapEditorStateToPreviewDetail';
import type { EditorPreviewInput } from 'src/utils/contentEditor/mapEditorStateToPreviewDetail';
import ExtranetContentBody from 'src/views/extranet/ExtranetContentBody';

type PreviewDevice = 'mobile' | 'desktop';

type ContentEditorPreviewProps = {
  open: boolean;
  onClose: () => void;
  draft: EditorPreviewInput;
};

/**
 * Drawer that previews the unsaved content editor draft in a Mobile or Desktop frame.
 */
export default function ContentEditorPreview({ open, onClose, draft }: ContentEditorPreviewProps) {
  const { t } = useTranslation();
  const { isMobile } = useDeviceDetect();
  const [device, setDevice] = useState<PreviewDevice>('mobile');

  const { detail, objectUrls } = useMemo(() => {
    if (!open) return { detail: null, objectUrls: [] };
    return mapEditorStateToPreviewDetail(draft);
  }, [open, draft]);

  useEffect(() => {
    if (!objectUrls.length) return undefined;
    return () => {
      objectUrls.forEach(url => {
        try {
          URL.revokeObjectURL(url);
        } catch {
          /* ignore */
        }
      });
    };
  }, [objectUrls]);

  useEffect(() => {
    if (open) setDevice('mobile');
  }, [open]);

  const deviceTabs: Array<{ key: PreviewDevice; label: string; icon: ReactElement }> = [
    { key: 'mobile', label: t('contentEditor.previewMobile'), icon: <MobilePreviewIcon width={21} height={28} /> },
    { key: 'desktop', label: t('contentEditor.previewDesktop'), icon: <DesktopPreviewIcon width={28} height={24} /> }
  ];

  return (
    <SwipeableDrawer
      anchor={isMobile ? 'bottom' : 'right'}
      open={open}
      onClose={onClose}
      onOpen={() => {}}
      PaperProps={{
        className: `content-editor__preview-drawer${isMobile ? ' content-editor__preview-drawer--mobile' : ''}`
      }}
      ModalProps={{ style: { zIndex: 6000 } }}>
      <div className="content-editor__preview">
        <div className="content-editor__preview-header">
          <div className="content-editor__preview-header-side">
            <Button secondary back type="button" label={t('contentEditor.backToEditor')} onClick={onClose} />
          </div>
          <h2 className="content-editor__preview-title">{t('contentEditor.preview')}</h2>
          <div className="content-editor__preview-header-side content-editor__preview-header-side--end">
            <div className="content-editor__preview-tabs" role="tablist" aria-label={t('contentEditor.preview')}>
              {deviceTabs.map(tab => (
                <button
                  key={tab.key}
                  type="button"
                  role="tab"
                  aria-selected={device === tab.key}
                  aria-label={tab.label}
                  title={tab.label}
                  className={`content-editor__preview-tab${
                    device === tab.key ? ' content-editor__preview-tab--active' : ''
                  }`}
                  onClick={() => setDevice(tab.key)}>
                  {tab.icon}
                </button>
              ))}
            </div>
          </div>
        </div>
        <div className="content-editor__preview-body">
          <div className={`content-editor__preview-frame content-editor__preview-frame--${device}`}>
            {detail ? <ExtranetContentBody detail={detail} /> : null}
          </div>
        </div>
      </div>
    </SwipeableDrawer>
  );
}
